import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import BooleanFloatingFilter from '../components/BooleanFloatingFilter';
import { gridTheme, defaultColDef, centeredCellStyle, centerAlignedCellStyle } from '../services/aggrid';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  GroupAdd as GroupAddIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import NoAccess from '../components/NoAccess';
import { Panel, Chip, Btn, Dot, Portrait, PageHead, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

function Members() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [members, setMembers] = useState([]);

  // Check permission - render early if no access
  if (!user?.permissions?.canViewMembers) {
    return <NoAccess feature="Corporation Members" requiredRole="Director or CEO" />;
  }
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [altsModalOpen, setAltsModalOpen] = useState(false);
  const [selectedAlts, setSelectedAlts] = useState([]);
  const [altSearchQuery, setAltSearchQuery] = useState('');
  const [mainFilter, setMainFilter] = useState('all');
  const [titleFilter, setTitleFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingNumAccounts, setEditingNumAccounts] = useState(null);
  const [numAccountsValue, setNumAccountsValue] = useState('');

  // Get unique main character names for filter dropdown
  const mainCharacterOptions = useMemo(() => {
    const mains = members.filter(m => m.isMain);
    return mains.map(m => ({ id: m.characterId, name: m.characterName }));
  }, [members]);

  // Get unique titles for filter dropdown (strip HTML)
  const titleOptions = useMemo(() => {
    const allTitles = new Set();
    members.forEach(m => {
      (m.titles || []).forEach(title => {
        const stripped = title.replace(/<[^>]*>/g, '');
        if (stripped) allTitles.add(stripped);
      });
    });
    return [...allTitles].sort();
  }, [members]);

  // Get unique roles for filter dropdown
  const roleOptions = useMemo(() => {
    const allRoles = new Set();
    members.forEach(m => {
      (m.roles || []).forEach(role => allRoles.add(role));
    });
    return [...allRoles].sort();
  }, [members]);

  // Get available alts (not main, not already assigned to a main)
  const availableAlts = useMemo(() => {
    return members.filter(m => !m.isMain && !m.mainCharacterId);
  }, [members]);

  // Filter available alts by search query
  const filteredAvailableAlts = useMemo(() => {
    if (!altSearchQuery.trim()) return availableAlts;
    const query = altSearchQuery.toLowerCase();
    return availableAlts.filter(alt =>
      alt.characterName.toLowerCase().includes(query)
    );
  }, [availableAlts, altSearchQuery]);

  // Menu handlers
  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  // Toggle main character flag
  const handleToggleMain = async () => {
    if (!selectedMember) return;

    try {
      const newIsMain = !selectedMember.isMain;
      await axios.patch(`${API_BASE_URL}/members/${selectedMember.characterId}/main`, {
        isMain: newIsMain
      });

      // Update local state
      setMembers(members.map(m =>
        m.characterId === selectedMember.characterId
          ? { ...m, isMain: newIsMain }
          : m
      ));
    } catch (err) {
      console.error('Error updating member:', err);
      setError(err.response?.data?.error || 'Failed to update member');
    }

    handleMenuClose();
  };

  // Toggle active status
  const handleToggleActive = async () => {
    if (!selectedMember) return;

    try {
      const newIsActive = selectedMember.isActive === false ? true : false;
      await axios.patch(`${API_BASE_URL}/members/${selectedMember.characterId}/active`, {
        isActive: newIsActive
      });

      // Update local state
      setMembers(members.map(m =>
        m.characterId === selectedMember.characterId
          ? { ...m, isActive: newIsActive }
          : m
      ));
    } catch (err) {
      console.error('Error updating member active status:', err);
      setError(err.response?.data?.error || 'Failed to update member status');
    }

    handleMenuClose();
  };

  // Open Add Alts modal
  const handleOpenAltsModal = () => {
    setSelectedAlts([]);
    setAltSearchQuery('');
    setAltsModalOpen(true);
    setAnchorEl(null); // Close menu but keep selectedMember
  };

  // Close Add Alts modal
  const handleCloseAltsModal = () => {
    setAltsModalOpen(false);
    setSelectedAlts([]);
    setAltSearchQuery('');
    setSelectedMember(null); // Clear selectedMember when modal closes
  };

  // Toggle alt selection
  const handleToggleAlt = (characterId) => {
    setSelectedAlts(prev =>
      prev.includes(characterId)
        ? prev.filter(id => id !== characterId)
        : [...prev, characterId]
    );
  };

  // Add selected alts to main
  const handleAddAlts = async () => {
    if (!selectedMember || selectedAlts.length === 0) return;

    try {
      await axios.post(`${API_BASE_URL}/members/${selectedMember.characterId}/alts`, {
        altCharacterIds: selectedAlts
      });

      // Update local state
      setMembers(members.map(m =>
        selectedAlts.includes(m.characterId)
          ? { ...m, mainCharacterId: selectedMember.characterId, mainCharacterName: selectedMember.characterName }
          : m
      ));

      handleCloseAltsModal();
    } catch (err) {
      console.error('Error adding alts:', err);
      setError(err.response?.data?.error || 'Failed to add alts');
    }
  };

  // Start editing numAccounts
  const handleStartEditNumAccounts = (member) => {
    setEditingNumAccounts(member.characterId);
    setNumAccountsValue(member.numAccounts?.toString() || '1');
  };

  // Save numAccounts
  const handleSaveNumAccounts = async (characterId) => {
    try {
      const num = parseInt(numAccountsValue);
      if (isNaN(num) || num < 1) {
        setError('Number of accounts must be at least 1');
        return;
      }

      await axios.patch(`${API_BASE_URL}/members/${characterId}/numAccounts`, {
        numAccounts: num
      });

      // Update local state
      setMembers(members.map(m =>
        m.characterId === characterId
          ? { ...m, numAccounts: num }
          : m
      ));

      setEditingNumAccounts(null);
      setNumAccountsValue('');
    } catch (err) {
      console.error('Error updating numAccounts:', err);
      setError(err.response?.data?.error || 'Failed to update number of accounts');
    }
  };

  // Cancel editing numAccounts
  const handleCancelEditNumAccounts = () => {
    setEditingNumAccounts(null);
    setNumAccountsValue('');
  };

  // Fetch members from MongoDB
  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/members`);
      setMembers(response.data);
    } catch (err) {
      console.error('Error fetching members:', err);

      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        console.log('Session expired, redirecting to login...');
        navigate('/?error=session_expired');
        return;
      }

      setError(err.response?.data?.error || 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  // Sync members from ESI
  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncMessage(null);

      const response = await axios.post(`${API_BASE_URL}/members/sync`);

      setSyncMessage({
        type: 'success',
        message: `Successfully synced ${response.data.count} member(s)${response.data.removed > 0 ? `, removed ${response.data.removed}` : ''}`
      });

      await fetchMembers();
    } catch (err) {
      console.error('Error syncing members:', err);

      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        console.log('Session expired, redirecting to login...');
        navigate('/?error=session_expired');
        return;
      }

      const errorMessage = err.response?.data?.error || 'Failed to sync members';
      setError(errorMessage);
      setSyncMessage({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // Strip HTML tags from title names (ESI returns titles with HTML formatting)
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  // Helper to get the main character name for sorting/grouping
  const getMainName = (member) => {
    if (member.isMain) return member.characterName;
    if (member.mainCharacterName) return member.mainCharacterName;
    return null;
  };

  // Filter members
  const filteredMembers = useMemo(() => {
    let filtered = members;

    // Apply main filter
    if (mainFilter === 'mains') {
      filtered = filtered.filter(m => m.isMain);
    } else if (mainFilter === 'unassigned') {
      filtered = filtered.filter(m => !m.isMain && !m.mainCharacterId);
    } else if (mainFilter !== 'all') {
      // Filter by specific main character ID
      filtered = filtered.filter(m =>
        m.characterId === mainFilter || m.mainCharacterId === mainFilter
      );
    }

    // Apply title filter
    if (titleFilter !== 'all') {
      filtered = filtered.filter(m =>
        m.titles?.some(title => stripHtml(title) === titleFilter)
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(m =>
        m.roles?.includes(roleFilter)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(member =>
        member.characterName.toLowerCase().includes(query) ||
        member.roles?.some(role => role.toLowerCase().includes(query)) ||
        member.titles?.some(title => title.toLowerCase().includes(query))
      );
    }

    // Sort by Main name (groups mains with their alts), then mains first, then by character name
    filtered.sort((a, b) => {
      const aMainName = getMainName(a);
      const bMainName = getMainName(b);

      // Unassigned characters go to the end
      if (!aMainName && bMainName) return 1;
      if (aMainName && !bMainName) return -1;

      // Group by main character name
      if (aMainName && bMainName && aMainName !== bMainName) {
        return aMainName.localeCompare(bMainName);
      }

      // Within same group, mains come first
      if (a.isMain && !b.isMain) return -1;
      if (!a.isMain && b.isMain) return 1;

      // Then sort by character name
      return a.characterName.localeCompare(b.characterName);
    });

    return filtered;
  }, [members, searchQuery, mainFilter, titleFilter, roleFilter]);

  // Format last online time as relative time
  const formatLastOnline = (date) => {
    if (!date) return 'Unknown';

    const now = new Date();
    const lastOnline = new Date(date);
    const diffMs = now - lastOnline;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 5) return 'Online';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) !== 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? 's' : ''} ago`;
  };

  // Derive online status from lastOnline (within 5 min = online)
  const isOnline = (date) => {
    if (!date) return false;
    const diffMins = Math.floor((new Date() - new Date(date)) / (1000 * 60));
    return diffMins < 5;
  };

  // Telemetry counts derived from real data
  const telemetry = useMemo(() => {
    const mains = members.filter(m => m.isMain);
    const online = members.filter(m => isOnline(m.lastOnline));
    const alts = members.filter(m => !m.isMain && m.mainCharacterId);
    const unassigned = members.filter(m => !m.isMain && !m.mainCharacterId);
    const onlinePct = mains.length > 0 ? Math.round((online.length / members.length) * 100) : 0;
    return { mains: mains.length, online: online.length, onlinePct, alts: alts.length, unassigned: unassigned.length };
  }, [members]);

  // Role chip tone
  const roleTone = (role) => {
    if (role === 'CEO') return 'amber';
    if (role === 'Director') return 'magenta';
    return '';
  };

  // AG Grid column definitions
  const columnDefs = useMemo(() => [
    {
      field: 'characterName',
      headerName: 'Character',
      flex: 2,
      minWidth: 240,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const m = params.data;
        return (
          <div className="cell-with-portrait" style={{ opacity: m.isActive === false ? 0.45 : 1 }}>
            <Portrait
              size={28}
              name={m.characterName}
              src={`https://images.evetech.net/characters/${m.characterId}/portrait?size=64`}
            />
            <span className="cell-strong" style={{ textDecoration: m.isActive === false ? 'line-through' : 'none' }}>
              {m.characterName}
            </span>
          </div>
        );
      }
    },
    {
      headerName: 'Status',
      width: 130,
      valueGetter: (p) => p.data.isActive === false ? 'Inactive' : (isOnline(p.data.lastOnline) ? 'Online' : 'Offline'),
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['equals'], defaultOption: 'equals' },
      floatingFilterComponent: BooleanFloatingFilter,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => {
        if (params.data.isActive === false) {
          return <Chip tone="danger">INACTIVE</Chip>;
        }
        return isOnline(params.data.lastOnline)
          ? <Chip tone="ok"><Dot tone="pulse" />ONLINE</Chip>
          : <Chip tone="ghost">OFFLINE</Chip>;
      }
    },
    {
      headerName: 'Main',
      width: 200,
      valueGetter: (p) => p.data.isMain ? p.data.characterName : (p.data.mainCharacterName || ''),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const m = params.data;
        if (m.isMain) {
          return <Chip tone="amber">&#9733; MAIN</Chip>;
        }
        if (m.mainCharacterName) {
          return <span className="cell-mute" style={{ fontSize: 11 }}>&#8627; {m.mainCharacterName}</span>;
        }
        return <span className="cell-mute">—</span>;
      }
    },
    {
      field: 'numAccounts',
      headerName: 'Accts',
      width: 110,
      filter: 'agNumberColumnFilter',
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => {
        const m = params.data;
        if (!m.isMain) return <span className="cell-mute">—</span>;
        if (editingNumAccounts === m.characterId) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                className="hud-input"
                type="number"
                value={numAccountsValue}
                onChange={(e) => setNumAccountsValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveNumAccounts(m.characterId);
                  if (e.key === 'Escape') handleCancelEditNumAccounts();
                }}
                min={1}
                style={{ width: 46, textAlign: 'center', padding: '2px 4px' }}
                autoFocus
              />
              <button
                className="btn"
                onClick={() => handleSaveNumAccounts(m.characterId)}
                style={{ padding: '2px 6px', fontSize: 11 }}
              >✓</button>
              <button
                className="btn"
                onClick={handleCancelEditNumAccounts}
                style={{ padding: '2px 6px', fontSize: 11 }}
              >✕</button>
            </div>
          );
        }
        return (
          <Tooltip title="Click to edit">
            <span
              className="chip"
              onClick={() => handleStartEditNumAccounts(m)}
              style={{ cursor: 'pointer', minWidth: 32, textAlign: 'center' }}
            >
              {m.numAccounts || 1}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'shipTypeName',
      headerName: 'Ship',
      width: 180,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      valueGetter: (p) => p.data.shipTypeName || '',
      cellRenderer: (params) => (
        params.data.shipTypeName
          ? <span className="cell-mute" style={{ fontSize: 11 }}>{params.data.shipTypeName}</span>
          : <span className="cell-mute">—</span>
      )
    },
    {
      field: 'lastOnline',
      headerName: 'Last Online',
      width: 160,
      valueGetter: (p) => p.data.lastOnline ? new Date(p.data.lastOnline) : null,
      filter: 'agDateColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const txt = formatLastOnline(params.data.lastOnline);
        const online = isOnline(params.data.lastOnline);
        return (
          <span className="mono" style={{
            fontSize: 11,
            color: online ? 'var(--ok)' : 'var(--text-2)'
          }}>
            {txt}
          </span>
        );
      }
    },
    {
      headerName: 'Titles',
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => (p.data.titles || []).map(t => stripHtml(t)).join(', '),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const titles = params.data.titles || [];
        if (titles.length === 0) return <span className="cell-mute">—</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, overflow: 'hidden', alignItems: 'flex-start' }}>
            {titles.slice(0, 2).map((title, i) => (
              <Chip key={i} tone="sm">{stripHtml(title)}</Chip>
            ))}
            {titles.length > 2 && (
              <Tooltip title={titles.slice(2).map(t => stripHtml(t)).join(', ')}>
                <Chip tone="sm ghost">+{titles.length - 2}</Chip>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      headerName: 'Roles',
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => (p.data.roles || []).map(r => r.replace(/_/g, ' ')).join(', '),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const roles = params.data.roles || [];
        if (roles.length === 0) return <span className="cell-mute">—</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, overflow: 'hidden', alignItems: 'flex-start' }}>
            {roles.slice(0, 2).map((role, i) => (
              <Chip key={i} tone={`sm ${roleTone(role)}`.trim()}>{role.replace(/_/g, ' ')}</Chip>
            ))}
            {roles.length > 2 && (
              <Tooltip title={roles.slice(2).map(r => r.replace(/_/g, ' ')).join(', ')}>
                <Chip tone="sm ghost">+{roles.length - 2}</Chip>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filter: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => (
        <button
          className="btn"
          style={{ padding: '4px 8px' }}
          onClick={(e) => handleMenuOpen(e, params.data)}
          title="Actions"
        >
          <MoreVertIcon style={{ fontSize: 16 }} />
        </button>
      )
    }
  ], [editingNumAccounts, numAccountsValue]);

  const getRowStyle = useCallback((params) => (
    params.data?.isMain ? { backgroundColor: 'rgba(51,181,229,0.10)' } : undefined
  ), []);

  return (
    <>
      <PageHead
        crumb="PERSONNEL // ROSTER"
        id={`${filteredMembers.length} OF ${members.length} VISIBLE`}
        title="Corporation Members"
        actions={
          <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync Roster'}
          </Btn>
        }
      />

      {/* Telemetry strip */}
      <div className="telemetry-strip">
        <div className="tile">
          <div className="k"><Dot tone="pulse" />ROSTER · MAINS</div>
          <div className="v">{telemetry.mains}<span className="unit">PILOTS</span></div>
        </div>
        <div className="tile ok">
          <div className="k"><Dot />ONLINE</div>
          <div className="v">{telemetry.online}<span className="unit">NOW</span></div>
          <div className={`delta ${telemetry.onlinePct > 0 ? 'up' : ''}`}>{telemetry.onlinePct}% PRESENCE</div>
        </div>
        <div className="tile amber">
          <div className="k">BOUND ALTS</div>
          <div className="v">{telemetry.alts}<span className="unit">CHR</span></div>
        </div>
        <div className="tile">
          <div className="k">UNASSIGNED</div>
          <div className="v">{telemetry.unassigned}<span className="unit">CHR</span></div>
        </div>
      </div>

      {/* Sync / error messages */}
      {syncMessage && (
        <div
          className={`chip ${syncMessage.type === 'success' ? 'ok' : 'danger'}`}
          style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
          onClick={() => setSyncMessage(null)}
        >
          {syncMessage.message} &nbsp;✕
        </div>
      )}
      {error && !syncMessage && (
        <div
          className="chip danger"
          style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}
          onClick={() => setError(null)}
        >
          {error} &nbsp;✕
        </div>
      )}

      {/* Pilot Manifest Panel */}
      <Panel
        title="Pilot Manifest"
        sub="LIVE · ESI"
        action={<Chip tone="ok"><Dot tone="pulse" />SYNCED</Chip>}
      >
        {/* Filter bar */}
        <div className="filter-bar">
          <div className="hud-input search" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' }}>
              {I.Search}
            </span>
            <input
              className="hud-input"
              placeholder="Search by name, role or title…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 32, width: '100%', maxWidth: 340 }}
            />
          </div>

          <select
            className="hud-select"
            value={mainFilter}
            onChange={(e) => setMainFilter(e.target.value)}
          >
            <option value="all">All Members</option>
            <option value="mains">Mains Only</option>
            <option value="unassigned">Unassigned Only</option>
            {mainCharacterOptions.map((main) => (
              <option key={main.id} value={main.id}>{main.name} &amp; Alts</option>
            ))}
          </select>

          <select
            className="hud-select"
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
          >
            <option value="all">All Titles</option>
            {titleOptions.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>

          <select
            className="hud-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>{role.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>

        {/* Grid or empty states */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240, color: 'var(--text-2)' }}>
            <Dot tone="pulse" /> &nbsp; LOADING ROSTER…
          </div>
        ) : members.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-2)' }}>
            <div style={{ marginBottom: 12, fontSize: 13 }}>NO MEMBERS ON RECORD</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 20 }}>
              Sync from ESI to populate the roster.
            </div>
            <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
              Sync Members
            </Btn>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-2)' }}>
            <div style={{ marginBottom: 8, fontSize: 13 }}>NO PILOTS MATCH FILTER</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Adjust search or filter parameters.</div>
          </div>
        ) : (
          <div className="hud-grid">
            <AgGridReact
              theme={gridTheme}
              rowData={filteredMembers}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              rowHeight={52}
              headerHeight={40}
              floatingFiltersHeight={36}
              pagination
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              quickFilterText={searchQuery}
              getRowStyle={getRowStyle}
              getRowId={(params) => params.data.characterId}
              animateRows
            />
          </div>
        )}
      </Panel>

      {/* Action Menu (kept as MUI - functional, risky to replace) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleToggleMain}>
          <ListItemIcon>
            {selectedMember?.isMain ? <StarBorderIcon fontSize="small" /> : <StarIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedMember?.isMain ? 'Remove Main Flag' : 'Make Main'}
          </ListItemText>
        </MenuItem>
        {selectedMember?.isMain && (
          <MenuItem onClick={handleOpenAltsModal}>
            <ListItemIcon>
              <GroupAddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Alts</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleToggleActive}>
          <ListItemIcon>
            {selectedMember?.isActive === false
              ? <ActiveIcon fontSize="small" color="success" />
              : <InactiveIcon fontSize="small" color="error" />}
          </ListItemIcon>
          <ListItemText>
            {selectedMember?.isActive === false ? 'Mark as Active' : 'Mark as Inactive'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Alts Modal (kept as MUI Dialog - functional, risky to replace) */}
      <Dialog
        open={altsModalOpen}
        onClose={handleCloseAltsModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Alts to {selectedMember?.characterName}</DialogTitle>
        <DialogContent>
          {availableAlts.length === 0 ? (
            <p style={{ color: 'var(--text-2)', padding: '8px 0' }}>
              No available characters to add as alts. Characters must not be marked as main
              and not already assigned to another main.
            </p>
          ) : (
            <>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <input
                  className="hud-input"
                  placeholder="Search characters…"
                  value={altSearchQuery}
                  onChange={(e) => setAltSearchQuery(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {filteredAvailableAlts.map((alt) => (
                  <div
                    key={alt.characterId}
                    onClick={() => handleToggleAlt(alt.characterId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--line)',
                      background: selectedAlts.includes(alt.characterId)
                        ? 'var(--accent-soft)'
                        : 'transparent'
                    }}
                  >
                    <Checkbox
                      checked={selectedAlts.includes(alt.characterId)}
                      onChange={() => handleToggleAlt(alt.characterId)}
                      size="small"
                    />
                    <Portrait
                      size={28}
                      name={alt.characterName}
                      src={`https://images.evetech.net/characters/${alt.characterId}/portrait?size=64`}
                    />
                    <span className="cell-strong">{alt.characterName}</span>
                  </div>
                ))}
                {filteredAvailableAlts.length === 0 && altSearchQuery && (
                  <p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '16px 0', fontSize: 12 }}>
                    No characters match your search.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Btn onClick={handleCloseAltsModal}>Cancel</Btn>
          <Btn
            tone="primary"
            onClick={handleAddAlts}
            disabled={selectedAlts.length === 0}
          >
            Add ({selectedAlts.length})
          </Btn>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Members;
